/**
 * Iter59 — DatosTab extraído de ClienteDetalle.jsx.
 *
 * Form de datos generales de la empresa. Recibe form/setForm + flags + catálogos
 * + handlers para los botones que viven en el padre (Perplexity, crear grupo,
 * cancelar/crear empresa nueva).
 */
import React, { useState, useRef, useEffect } from 'react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  MapPin, Phone, Plus, CheckCircle2, Sparkles,
  GitBranch, Warehouse, Truck, Loader2,
} from 'lucide-react';
import { FieldError } from '../ui/FieldError';
import { Campo, Select, TIPOS, ESTADOS, NATURALEZAS, AMBITOS } from './_shared';
import { req } from '../../services/api';

export const DatosTab = ({
  form, setForm,
  edit,
  formErrors,
  err,
  saving,
  isNuevo,
  isAdmin,
  entidad,
  materiales,
  grupos,
  comerciales,
  paisesExtra,
  provinciasPorPais,
  onGuardar,
  onAbrirGrupo,
  onAbrirPplx,
  onCancelarNuevo,
}) => {
  const [parentQuery, setParentQuery] = useState('');
  const [parentResultados, setParentResultados] = useState([]);
  const [parentBuscando, setParentBuscando] = useState(false);
  const parentTimer = useRef(null);
  const [grupoSugerido, setGrupoSugerido] = useState(null);
  const [sugiendoGrupo, setSugiendoGrupo] = useState(false);

  const buscarParent = (q) => {
    setParentQuery(q);
    clearTimeout(parentTimer.current);
    if (!q.trim()) { setParentResultados([]); return; }
    parentTimer.current = setTimeout(async () => {
      setParentBuscando(true);
      try {
        const d = await req(`/api/entidades/autocomplete?q=${encodeURIComponent(q.trim())}&limit=6`);
        setParentResultados(Array.isArray(d) ? d : (d.items || []));
      } catch (_) { setParentResultados([]); }
      finally { setParentBuscando(false); }
    }, 300);
  };

  useEffect(() => {
    const esDistribuidor = form.tipo === 'distribuidor' || (form.tipos_adicionales || []).includes('distribuidor');
    if (!esDistribuidor || form.grupo_id || !form.razon_social) {
      setGrupoSugerido(null);
      return;
    }
    let cancelled = false;
    setSugiendoGrupo(true);
    req(`/api/entidades/sugerir-grupo?nombre=${encodeURIComponent(form.razon_social)}`)
      .then((d) => { if (!cancelled) setGrupoSugerido(d?.grupo ?? null); })
      .catch(() => { if (!cancelled) setGrupoSugerido(null); })
      .finally(() => { if (!cancelled) setSugiendoGrupo(false); });
    return () => { cancelled = true; };
  }, [form.tipo, form.tipos_adicionales, form.razon_social, form.grupo_id]);

  return (
  <Card className="p-6 border-ink-200 bg-white" data-testid="tab-datos-content">
    <form onSubmit={onGuardar} noValidate className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Campo label="Razón social *" required>
        <Input value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} disabled={!edit} data-testid="f-razon-social" aria-invalid={!!formErrors.razon_social} />
        <FieldError message={formErrors.razon_social} testid="err-razon-social" />
      </Campo>
      <Campo label="Nombre comercial">
        <Input value={form.nombre_comercial} onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })} disabled={!edit} />
      </Campo>
      <Campo label="CIF">
        <Input value={form.cif} onChange={(e) => setForm({ ...form, cif: e.target.value })} disabled={!edit} data-testid="f-cif" />
      </Campo>
      <Campo label="Código cliente ERP">
        <Input value={form.codigo_cliente_erp} onChange={(e) => setForm({ ...form, codigo_cliente_erp: e.target.value })} disabled={!edit} />
      </Campo>
      <Campo label="Tipo">
        <Select value={form.tipo} disabled={!edit} onChange={(v) => setForm({ ...form, tipo: v })} options={TIPOS} testid="f-tipo" />
      </Campo>
      <Campo label="Estado">
        <Select value={form.estado} disabled={!edit} onChange={(v) => setForm({ ...form, estado: v })} options={ESTADOS} testid="f-estado" />
      </Campo>
      <Campo label="Naturaleza jurídica">
        <Select value={form.naturaleza_juridica} disabled={!edit} onChange={(v) => setForm({ ...form, naturaleza_juridica: v })} options={NATURALEZAS} />
      </Campo>
      {(form.tipo === 'distribuidor' || (form.tipos_adicionales || []).includes('distribuidor')) && (
        <Campo label="Ámbito del distribuidor">
          <Select value={form.ambito_distribuidor} disabled={!edit} onChange={(v) => setForm({ ...form, ambito_distribuidor: v })} options={AMBITOS} />
        </Campo>
      )}
      <Campo label="Grupo empresarial">
        {edit ? (
          <div className="flex items-center gap-2">
            <Select
              value={form.grupo_id} disabled={!edit}
              onChange={(v) => setForm({ ...form, grupo_id: v })}
              options={[{ v: '', l: '— Sin grupo —' }, ...grupos.map((g) => ({ v: g.id, l: g.nombre }))]}
            />
            {isAdmin && (
              <Button type="button" variant="outline" onClick={onAbrirGrupo} className="h-10 whitespace-nowrap" data-testid="btn-crear-grupo-inline">
                <Plus className="w-4 h-4 mr-1" /> Nuevo
              </Button>
            )}
          </div>
        ) : (
          <Select value={form.grupo_id} disabled onChange={() => {}} options={[{ v: '', l: '— Sin grupo —' }, ...grupos.map((g) => ({ v: g.id, l: g.nombre }))]} />
        )}
      </Campo>
      <Campo label="Comercial asignado">
        <Select
          value={form.comercial_asignado_id} disabled={!edit}
          onChange={(v) => setForm({ ...form, comercial_asignado_id: v })}
          options={[{ v: '', l: '— Sin asignar —' }, ...comerciales.map((c) => ({ v: c.id, l: `${c.nombre} ${c.apellidos || ''}`.trim() }))]}
        />
      </Campo>
      {form.tipo === 'gestora' && (
        <Campo label="División interna">
          <Input
            value={form.division || ''}
            onChange={(e) => setForm({ ...form, division: e.target.value })}
            disabled={!edit}
            placeholder="Ej: Levante, Centro, Norte…"
            data-testid="input-division"
          />
        </Campo>
      )}
      <div className="md:col-span-2 flex flex-wrap items-center gap-4 text-sm">
        <span className="text-xs font-medium text-ink-600 mr-1">Rol comercial: *</span>
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            className="accent-emerald-500"
            checked={!!form.es_cliente}
            disabled={!edit}
            onChange={(e) => setForm({ ...form, es_cliente: e.target.checked })}
            data-testid="f-es-cliente"
          />
          <span className="text-ink-700">Cliente</span>
        </label>
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            className="accent-violet-500"
            checked={!!form.es_proveedor}
            disabled={!edit}
            onChange={(e) => setForm({ ...form, es_proveedor: e.target.checked })}
            data-testid="f-es-proveedor"
          />
          <span className="text-ink-700">Proveedor</span>
        </label>
        <FieldError message={formErrors.rol_comercial} testid="err-rol-comercial" />
      </div>
      <div className="md:col-span-2 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          id="f-es-ute"
          className="accent-brand-500"
          checked={!!form.es_ute}
          disabled={!edit}
          onChange={(e) => setForm({ ...form, es_ute: e.target.checked })}
          data-testid="f-es-ute"
        />
        <label htmlFor="f-es-ute" className="text-ink-700">Esta entidad es una <span className="font-semibold">UTE</span> (unión temporal de empresas)</label>
      </div>

      <div className="md:col-span-2 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          id="f-es-distribuidor"
          className="accent-orange-500"
          checked={form.tipo === 'distribuidor' || !!(form.tipos_adicionales || []).includes('distribuidor')}
          disabled={!edit || form.tipo === 'distribuidor'}
          onChange={(e) => {
            if (form.tipo === 'distribuidor') return;
            const prev = form.tipos_adicionales || [];
            const next = e.target.checked
              ? [...prev.filter((t) => t !== 'distribuidor'), 'distribuidor']
              : prev.filter((t) => t !== 'distribuidor');
            setForm({ ...form, tipos_adicionales: next });
          }}
          data-testid="f-es-distribuidor"
        />
        <label htmlFor="f-es-distribuidor" className="text-ink-700">
          Esta entidad es un <span className="font-semibold">distribuidor</span>
          {form.tipo === 'distribuidor' && <span className="text-ink-400 text-xs ml-1">(tipo principal)</span>}
        </label>
      </div>
      {sugiendoGrupo && (
        <p className="md:col-span-2 text-xs text-ink-400 animate-pulse">Buscando grupo sugerido…</p>
      )}
      {grupoSugerido && !form.grupo_id && edit && (
        <div className="md:col-span-2 flex items-center gap-3 px-3 py-2.5 rounded-md bg-brand-50 border border-brand-200 text-sm">
          <span className="text-brand-700 flex-1">
            Este distribuidor pertenece al grupo <strong>{grupoSugerido.nombre}</strong>. ¿Asignar automáticamente?
          </span>
          <Button
            type="button" size="sm" variant="outline"
            className="border-brand-300 text-brand-700 hover:bg-brand-100 shrink-0"
            onClick={() => { setForm({ ...form, grupo_id: grupoSugerido.id }); setGrupoSugerido(null); }}
          >
            Sí, asignar
          </Button>
          <button
            type="button"
            className="text-xs text-ink-400 hover:text-ink-600 shrink-0"
            onClick={() => setGrupoSugerido(null)}
          >
            No
          </button>
        </div>
      )}

      {form.tipo === 'fabricante' && (
        <Campo label="Materiales que fabrica" wide>
          <div className="flex flex-wrap gap-2" data-testid="f-materiales">
            {materiales.length === 0 ? (
              <span className="text-xs text-ink-400">
                No hay materiales definidos. Pide al admin que los cree en Organigrama → Materiales que fabrica.
              </span>
            ) : materiales.map((m) => {
              const checked = form.materiales_fabricados?.includes(m.valor);
              return (
                <button
                  key={m.valor}
                  type="button"
                  disabled={!edit}
                  onClick={() => {
                    if (!edit) return;
                    const set = new Set(form.materiales_fabricados || []);
                    if (checked) set.delete(m.valor); else set.add(m.valor);
                    setForm({ ...form, materiales_fabricados: [...set] });
                  }}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition-colors ${
                    checked
                      ? 'bg-brand-50 border-brand-300 text-brand-700'
                      : 'bg-white border-ink-200 text-ink-600 hover:bg-ink-50 disabled:hover:bg-white disabled:opacity-60'
                  }`}
                  data-testid={`f-material-${m.valor}`}
                >
                  {checked && <CheckCircle2 className="w-3 h-3" />}
                  {m.label}
                </button>
              );
            })}
          </div>
        </Campo>
      )}

      <div className="md:col-span-2 border-t border-ink-100 pt-4 mt-2">
        <h3 className="font-heading font-semibold text-ink-800 mb-3 flex items-center gap-2"><MapPin className="w-4 h-4" /> Ubicación</h3>
      </div>
      <Campo label="País *" required>
        <Select
          value={form.pais} disabled={!edit}
          onChange={(v) => setForm({ ...form, pais: v })}
          options={[
            { v: 'ES', l: '🇪🇸 España' },
            { v: 'PT', l: '🇵🇹 Portugal' },
            ...paisesExtra.map((p) => ({ v: p.codigo, l: `${p.nombre} (${p.codigo})` })),
          ]}
          testid="f-pais"
        />
      </Campo>
      <Campo label="Provincia / Distrito *" required>
        {(form.pais === 'ES' || form.pais === 'PT') ? (
          <Select
            value={form.provincia} disabled={!edit}
            onChange={(v) => setForm({ ...form, provincia: v })}
            options={[
              { v: '', l: '— Elegir —' },
              ...(provinciasPorPais[form.pais] || []).map((p) => ({ v: p, l: p })),
            ]}
            testid="f-provincia"
          />
        ) : (
          <Input
            value={form.provincia}
            onChange={(e) => setForm({ ...form, provincia: e.target.value })}
            disabled={!edit}
            placeholder="Región/Estado (texto libre)"
            data-testid="f-provincia"
            aria-invalid={!!formErrors.provincia}
          />
        )}
        <FieldError message={formErrors.provincia} testid="err-provincia" />
      </Campo>
      <Campo label="Localidad">
        <Input value={form.localidad} onChange={(e) => setForm({ ...form, localidad: e.target.value })} disabled={!edit} />
      </Campo>
      <Campo label="Dirección">
        <Input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} disabled={!edit} />
      </Campo>
      <Campo label="Código postal">
        <Input value={form.codigo_postal} onChange={(e) => setForm({ ...form, codigo_postal: e.target.value })} disabled={!edit} />
      </Campo>
      {entidad?.latitud != null && entidad?.longitud != null && !edit && (
        <div className="md:col-span-2 text-xs text-ink-500">
          <MapPin className="inline w-3 h-3 mr-1" />
          Coordenadas: <span className="font-mono">{entidad.latitud.toFixed(5)}, {entidad.longitud.toFixed(5)}</span>
          <span className="ml-2 text-emerald-600">geocodificado automáticamente</span>
        </div>
      )}

      <div className="md:col-span-2 border-t border-ink-100 pt-4 mt-2">
        <h3 className="font-heading font-semibold text-ink-800 mb-3 flex items-center gap-2"><Phone className="w-4 h-4" /> Contacto corporativo</h3>
      </div>
      <Campo label="Teléfono"><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} disabled={!edit} /></Campo>
      <Campo label="Email">
        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!edit} data-testid="f-email-empresa" aria-invalid={!!formErrors.email} />
        <FieldError message={formErrors.email} testid="err-email-empresa" />
      </Campo>
      <Campo label="Web" wide>
        <Input value={form.web} onChange={(e) => setForm({ ...form, web: e.target.value })} disabled={!edit} placeholder="https://..." />
      </Campo>
      {form.tipo === 'otro' && (
        <Campo label="Notas de oficina técnica" wide>
          <Textarea rows={2} value={form.oficina_tecnica_notas} onChange={(e) => setForm({ ...form, oficina_tecnica_notas: e.target.value })} disabled={!edit} />
        </Campo>
      )}
      {/* ── Sección Delegaciones ──────────────────────────────── */}
      <div className="md:col-span-2 border-t border-ink-100 pt-4 mt-2">
        <h3 className="font-heading font-semibold text-ink-800 mb-3 flex items-center gap-2">
          <GitBranch className="w-4 h-4" /> Estructura de delegaciones
        </h3>
      </div>

      {/* Toggle es_delegacion */}
      <div className="md:col-span-2 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          id="f-es-delegacion"
          className="accent-brand-500"
          checked={!!form.es_delegacion}
          disabled={!edit}
          onChange={(e) => setForm({ ...form, es_delegacion: e.target.checked, parent_entidad_id: e.target.checked ? form.parent_entidad_id : '' })}
          data-testid="f-es-delegacion"
        />
        <label htmlFor="f-es-delegacion" className="text-ink-700">
          Esta entidad es una <span className="font-semibold">delegación</span> de otra empresa
        </label>
      </div>

      {/* Empresa madre — solo visible si es_delegacion */}
      {form.es_delegacion && (
        <Campo label="Empresa matriz *" wide>
          <div className="relative">
            {form.parent_entidad_id && entidad?.parent_entidad_nombre ? (
              <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-emerald-200 bg-emerald-50 text-sm">
                <span className="flex-1 text-emerald-800 font-medium truncate">{entidad.parent_entidad_nombre}</span>
                {edit && (
                  <button type="button" className="text-ink-400 hover:text-ink-700 text-xs shrink-0"
                    onClick={() => { setForm({ ...form, parent_entidad_id: '' }); setParentQuery(''); }}>
                    ✕ Cambiar
                  </button>
                )}
              </div>
            ) : (
              <>
                <Input
                  value={form.parent_entidad_id ? '' : parentQuery}
                  onChange={(e) => buscarParent(e.target.value)}
                  disabled={!edit}
                  placeholder="Buscar empresa matriz por nombre…"
                  data-testid="f-parent-entidad"
                />
                {parentBuscando && <p className="text-[10px] text-ink-400 mt-0.5">Buscando…</p>}
                {parentResultados.length > 0 && (
                  <div className="absolute top-full left-0 right-0 z-[9999] bg-white border border-ink-200 rounded shadow-md mt-0.5 max-h-44 overflow-y-auto">
                    {parentResultados.map((r) => (
                      <button key={r.id} type="button"
                        className="w-full text-left px-3 py-2 text-xs hover:bg-brand-50 border-b border-ink-50 last:border-0"
                        onClick={() => { setForm({ ...form, parent_entidad_id: r.id }); setParentQuery(''); setParentResultados([]); }}>
                        <span className="font-medium text-ink-900">{r.nombre_comercial || r.razon_social}</span>
                        {r.cif && <span className="ml-1 text-ink-400">{r.cif}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </Campo>
      )}

      {/* Flags de stock: depósito nuestro y almacén logístico */}
      <div className="md:col-span-2 flex flex-wrap gap-4">
        <label className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition select-none ${
          form.tiene_deposito_nuestro
            ? 'border-amber-300 bg-amber-50 text-amber-800'
            : 'border-ink-200 bg-white text-ink-600 hover:border-ink-300'
        } ${!edit ? 'opacity-60 cursor-default' : ''}`}>
          <input
            type="checkbox"
            className="accent-amber-500"
            checked={!!form.tiene_deposito_nuestro}
            disabled={!edit}
            onChange={(e) => setForm({ ...form, tiene_deposito_nuestro: e.target.checked })}
            data-testid="f-tiene-deposito"
          />
          <Warehouse className="w-4 h-4 shrink-0" />
          <div>
            <p className="text-sm font-medium leading-none">Depósito nuestro</p>
            <p className="text-[11px] mt-0.5 opacity-70">Tenemos stock físico depositado en esta delegación (AMD)</p>
          </div>
        </label>

        <label className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition select-none ${
          form.es_almacen_logistico
            ? 'border-blue-300 bg-blue-50 text-blue-800'
            : 'border-ink-200 bg-white text-ink-600 hover:border-ink-300'
        } ${!edit ? 'opacity-60 cursor-default' : ''}`}>
          <input
            type="checkbox"
            className="accent-blue-500"
            checked={!!form.es_almacen_logistico}
            disabled={!edit}
            onChange={(e) => setForm({ ...form, es_almacen_logistico: e.target.checked })}
            data-testid="f-es-almacen-logistico"
          />
          <Truck className="w-4 h-4 shrink-0" />
          <div>
            <p className="text-sm font-medium leading-none">Almacén logístico del cliente</p>
            <p className="text-[11px] mt-0.5 opacity-70">Distribuye desde aquí a sus otras delegaciones</p>
          </div>
        </label>
      </div>

      <Campo label="Notas internas" wide>
        <Textarea rows={3} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} disabled={!edit} />
      </Campo>

      {err && <div className="md:col-span-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>}

      {isNuevo && (
        <div className="md:col-span-2 flex justify-between items-center pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onAbrirPplx}
            className="border-violet-200 text-violet-700 hover:bg-violet-50"
            data-testid="btn-pplx-empresa-nuevo"
            disabled={!form.razon_social && !form.cif}
            title={!form.razon_social && !form.cif ? 'Introduce al menos la razón social o CIF para buscar' : ''}
          >
            <Sparkles className="w-4 h-4 mr-1.5" /> Buscar con Perplexity
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onCancelarNuevo}>Cancelar</Button>
            <Button type="submit" className="bg-brand-500 hover:bg-brand-600 text-white" disabled={saving} data-testid="btn-crear">
              {saving ? 'Creando…' : 'Crear empresa'}
            </Button>
          </div>
        </div>
      )}
    </form>
  </Card>
  );
};

export default DatosTab;
